import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

import { ScenarioSetup } from '../ScenarioSetup.types'

const { Standard, Obstacle, TurningPoint, Bonus } = CollectibleType

const config: ScenarioSetup = {
  backgroundColor: 0xfed1c8,
  lanesColor: 0xe2b577,
  lanesExtraColor: 0xfed1c8,
  lanesTextureId: 'rule',

  clearColor: 0xfff4e5,
  smokeColor: 0xf7ead8,

  playerColor: 0xfed1c8,
  playerGoodColor: 0xfce5ab,
  playerWrongColor: 0xfe8769,

  audioSfx: { path: 'sfx_audio/sfx.json' },
  audio: {
    en: {
      path: 'pedro_2/audios/en/bundle_1.json',
      subtitles: 'pedro_2/audios/en/bundle_1.vtt',
    },
  },

  collectibles: {
    resources: [{ request: 'pedro_2/collectible.glb' }],

    items: [
      {
        id: 'bubble_1',
        type: Standard,
      },
      {
        id: 'bubble_2',
        type: Standard,
      },
      {
        id: 'bubble_3',
        type: Standard,
      },
      {
        id: 'fake_bubble_1',
        type: Standard,
      },
      {
        id: 'fake_bubble_2',
        type: Standard,
      },
      {
        id: 'fake_bubble_3',
        type: Standard,
      },
      {
        id: 'carrot',
        type: Obstacle,
        audio: 'p2_food_teacher',
      },
      {
        id: 'brocoli',
        type: Obstacle,
        audio: 'p2_apple_A',
      },
      {
        id: 'pizza',
        type: Obstacle,
        audio: 'p2_food_teacher',
      },

      {
        id: 'apple',
        type: Obstacle,
        audio: 'p2_apple_A',
      },
      {
        id: 'clown',
        type: Obstacle,
        audio: 'p2_clown_joke_A_combined',
      },
      {
        id: 'handspinner',
        type: Obstacle,
        chill: true,
        bonus: BonusType.Relax,
      },
      {
        id: 'fire_turbo',
        type: Bonus,
        bonus: BonusType.Focus,
      },
      {
        id: 'pixelated_brain',
        type: Bonus,
        bonus: BonusType.Pixelated,
        audio: 'p2_pixelated_brain_A',
      },
    ],
  },

  particles: {
    resources: [
      { id: 'default', request: 'common/particles.glb' },
      { id: 'narnia', request: 'common/particles_music.glb', scale: 0.5 },
    ],
  },

  background: {
    resources: [
      {
        id: 'background',
        request: 'pedro_2/background.glb',
      },
      {
        id: 'erlenmeyer',
        request: 'pedro_2/erlenmeyer.glb',
        animation: { framerate: 12, loop: true },
      },
      {
        id: 'pencilcase',
        request: 'pedro_2/pencilcase.glb',
        animation: { framerate: 12, loop: false },
      },
      {
        id: 'pizza',
        request: 'pedro_2/pizza.glb',
        animation: { framerate: 12, loop: true },
      },
    ],

    items: [
      { id: 'pen_pot' },
      { id: 'apple' },
      { id: 'brocoli' },
      { id: 'carrot' },
      { id: 'sharpener' },
      { id: 'erlenmeyer' },
      { id: 'pencilcase' },
      { id: 'pen_01' },
      { id: 'pen_02' },
      { id: 'pizza' },
    ],
  },

  sequences: [
    //#region phase_1
    {
      id: 'phase_1',

      goals: [['bubble_1']],
      view: { variant: SequenceViewVariant.Bubble },

      track: {
        type: 'procedural',
        collectibles: ['bubble_1', 'bubble_2', 'bubble_3', 'fake_bubble_1', 'fake_bubble_2', 'fake_bubble_3'],
      },

      scrolling: ['pen_pot', 'erlenmeyer', 'pencilcase', 'sharpener', 'pen_01', 'pen_02'],
    },
    //#endregion

    //#region phase_2
    {
      id: 'phase_2',

      goals: [['bubble_1']],
      view: { variant: SequenceViewVariant.Bubble },

      track: {
        type: 'procedural',
        rate: 1.75,
        collectibles: ['bubble_1', 'bubble_2', 'bubble_3', 'fake_bubble_1', 'fake_bubble_2', 'fake_bubble_3'],
        barriers: ['clown'],
      },

      scrolling: ['pen_pot', 'erlenmeyer', 'pencilcase', 'sharpener', 'pen_01', 'pen_02'],
    },
    //#endregion

    //#region phase_3
    {
      id: 'phase_3',

      goals: [['bubble_2']],
      view: { variant: SequenceViewVariant.Bubble },

      track: {
        type: 'procedural',
        rate: 1.75,
        collectibles: ['bubble_1', 'bubble_2', 'bubble_3', 'fake_bubble_1', 'fake_bubble_2', 'fake_bubble_3'],
        barriers: ['pixelated_brain'],
      },

      scrolling: ['pen_pot', 'brocoli', 'erlenmeyer', 'pencilcase', 'sharpener', 'pen_01', 'pen_02'],
    },
    //#endregion

    //#region phase_4
    {
      id: 'phase_4',

      goals: [['bubble_3']],
      view: { variant: SequenceViewVariant.Bubble },

      track: {
        type: 'procedural',
        mandatoryBonus: BonusType.Focus,
        rate: 1.75,
        collectibles: [
          'bubble_1',
          'bubble_2',
          'bubble_3',
          'fake_bubble_1',
          'fake_bubble_2',
          'fake_bubble_3',
          'fire_turbo',
        ],
      },

      scrolling: ['pen_pot', 'brocoli', 'erlenmeyer', 'carrot', 'apple'],
    },
    //#endregion

    //#region phase_5
    {
      id: 'phase_5',

      goals: [['bubble_2']],
      view: { variant: SequenceViewVariant.Bubble },

      stop: {
        preserveCollectibles: true,
        alterJauge: -1,
      },

      track: {
        type: 'procedural',
        rate: 1.75,
        collectibles: ['bubble_1', 'bubble_2', 'bubble_3', 'fake_bubble_1', 'fake_bubble_2', 'fake_bubble_3'],
        barriers: ['handspinner'],
      },

      scrolling: ['pen_pot', 'brocoli', 'erlenmeyer', 'carrot', 'apple'],
    },
    //#endregion

    //#region phase_6
    {
      id: 'phase_6',

      condition: { id: 'spawned-count', count: 20 },

      goals: [['bubble_2']],
      view: { variant: SequenceViewVariant.Bubble },

      track: {
        type: 'procedural',
        warmupTime: 0,
        rate: 1.25,
        collectibles: ['bubble_1', 'bubble_3', 'carrot', 'brocoli', 'apple'],
      },

      scrolling: ['pen_pot', 'brocoli', 'erlenmeyer', 'carrot', 'apple'],
    },
    //#endregion
  ],

  turningPoints: [],

  audioSegments: [
    {
      id: 'audio_intro',
      audios: ['p2_intro'],
    },
    {
      id: 'audio_teacher',
      audios: ['p2_intro_teacher'],
    },
    {
      id: 'audio_clown',
      audios: ['p2_clown_A'],
    },
    {
      id: 'audio_food',
      audios: ['p2_pizza'],
    },
    {
      id: 'audio_narnia',
      audios: [
        'p2_narnia_turningpoint_woah',
        'p2_narnia_turningpoint_when_drift',
        'p2_teacher_intervenes',
        'p2_pedro_comes_back',
      ],
      track: {
        type: 'procedural',
        rate: 1.5,
        warmupTime: 0,
        variation: 1.2,
        collectibles: ['carrot', 'brocoli', 'apple'],
      },
    },
  ],
}

export default config
